import { LoadingState } from '@/components/primitives/LoadingState'
import { PageHeader } from '@/components/primitives/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  LLM_AGENT_KINDS,
  type LlmAgentKind,
  fetchLlmAgents,
  fetchScrapersStatus,
  fetchSystemTasks,
  refreshMetricsNow,
  sendTestEmail,
  toggleLlmAgent
} from '@/lib/p5'
import { notifyError } from '@/lib/query'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  Cpu,
  Database,
  Loader2,
  Mail,
  RefreshCw,
  XCircle
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

// ---- Scrapers ------------------------------------------------------------

export function ScrapersPage() {
  const status = useQuery({
    queryKey: ['scrapers-status'],
    queryFn: fetchScrapersStatus,
    refetchInterval: 60_000
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Scrapers"
        description="Per-source ingestion health. Red badge means a run failed in the last 24 hours."
      />

      {status.isLoading ? (
        <LoadingState variant="tiles" rows={3} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(status.data?.sources ?? {}).map(([source, s]) => (
            <Card key={source} className="rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <Database className="size-4 text-primary" />
                <span className="font-mono text-sm">{source}</span>
                {!s.available ? (
                  <Badge variant="outline" className="ml-auto">
                    unavailable
                  </Badge>
                ) : (s.failed_last_24h ?? 0) > 0 ? (
                  <Badge variant="destructive" className="ml-auto">
                    {s.failed_last_24h} failed
                  </Badge>
                ) : (
                  <Badge variant="default" className="ml-auto">
                    healthy
                  </Badge>
                )}
              </div>
              {s.available && (
                <div className="mt-3 grid gap-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Running now</span>
                    <span className="tabular-nums">{s.running ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last run</span>
                    <span>
                      {s.last_run_at
                        ? new Date(s.last_run_at).toLocaleString()
                        : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last status</span>
                    <span className="font-mono">
                      {s.last_run_status ?? '—'}
                    </span>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

// ---- LLM Agents ----------------------------------------------------------

export function LlmAgentsPage() {
  const [busy, setBusy] = useState<LlmAgentKind | null>(null)
  const status = useQuery({
    queryKey: ['llm-agent-controls'],
    queryFn: fetchLlmAgents
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="LLM Agents"
        description="Control which BlackChatGPT assistants are available."
      />

      {status.isLoading ? (
        <LoadingState variant="tiles" rows={6} />
      ) : status.isError ? (
        <button
          type="button"
          onClick={() => void status.refetch()}
          className="rounded-[36px] border border-neutral-200 p-4 dark:border-neutral-800"
        >
          Could not load assistants. Retry
        </button>
      ) : (
        <>
          {status.data?.enabled === false && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              AI chat is disabled by the master switch.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {LLM_AGENT_KINDS.map((kind) => (
              <Card
                key={kind}
                className="rounded-[36px] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold capitalize">
                      {kind} agent
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-400">
                      Toggling off hides this agent from chat for all users.
                    </div>
                  </div>
                  <Switch
                    aria-label={`Enable ${kind} assistant`}
                    checked={status.data?.agents[kind] ?? false}
                    disabled={busy !== null}
                    onCheckedChange={async (v) => {
                      setBusy(kind)
                      try {
                        await toggleLlmAgent(kind, v)
                        await status.refetch()
                        toast.success(
                          `${kind} agent ${v ? 'enabled' : 'disabled'}`
                        )
                      } catch (err) {
                        notifyError('Toggle failed', err)
                      } finally {
                        setBusy(null)
                      }
                    }}
                  />
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ---- Email & Notifications ----------------------------------------------

export function EmailPage() {
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('XOBLACK admin test')
  const [body, setBody] = useState('This is a test email from the admin panel.')

  const send = useMutation({
    mutationFn: () => sendTestEmail({ to, subject, body }),
    onSuccess: () => toast.success(`Test email sent to ${to}`),
    onError: (err) => notifyError('Email failed', err)
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Email & Notifications"
        description="Send a test email through the configured backend to verify SMTP / SendGrid wiring."
      />

      <Card className="rounded-2xl p-5">
        <div className="mb-3 flex items-center gap-2">
          <Mail className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Test email</h3>
        </div>
        <div className="grid max-w-lg gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="email-to">To</Label>
            <Input
              id="email-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="email-body">Body</Label>
            <Textarea
              id="email-body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div>
            <Button
              disabled={send.isPending || !to.trim() || !to.includes('@')}
              onClick={() => send.mutate()}
            >
              {send.isPending && <Loader2 className="size-4 animate-spin" />}
              Send test
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

// ---- System Tasks --------------------------------------------------------

export function SystemTasksPage() {
  const tasks = useQuery({
    queryKey: ['system-tasks'],
    queryFn: fetchSystemTasks,
    refetchInterval: 30_000
  })

  const refresh = useMutation({
    mutationFn: () => refreshMetricsNow(),
    onSuccess: () => toast.success('Metrics refresh queued'),
    onError: (err) => notifyError('Refresh failed', err)
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="System Tasks"
        description="Celery queue depth + Redis health + last metrics refresh time."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Refresh metrics now
          </Button>
        }
      />

      <div className="grid gap-3 md:grid-cols-3">
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Cpu className="size-3" /> Celery queue depth
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">
            {tasks.data?.queue_depth ?? '—'}
          </div>
        </Card>
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            Redis
          </div>
          <div className="mt-2 flex items-center gap-2 text-base font-medium">
            {tasks.data?.redis_ok ? (
              <>
                <CheckCircle2 className="size-4 text-emerald-600" /> Healthy
              </>
            ) : (
              <>
                <XCircle className="size-4 text-destructive" /> Unreachable
              </>
            )}
          </div>
        </Card>
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            Last metrics refresh
          </div>
          <div className="mt-2 text-sm">
            {tasks.data?.last_metrics_refresh_at
              ? new Date(tasks.data.last_metrics_refresh_at).toLocaleString()
              : '—'}
          </div>
        </Card>
      </div>
    </div>
  )
}
